import { useEffect, useRef, type ChangeEvent, type ClipboardEvent, type KeyboardEvent } from 'react'
import { useTranslation } from '@/i18n'

/**
 * 6 桁 OTP 入力用 セル UI (ookey-browser `TwoFactorScreen` を web に移植)。
 *
 * 動作:
 *   - 各セルは 1 文字。数字のみ。入力で次セルへ自動 focus、Backspace で前へ戻る。
 *   - paste で 6 桁一括投入対応 (authenticator app からの直接 paste)。
 *   - 6 桁全て揃ったら `onAutoSubmit` を発火 (form 側で `requestSubmit()` を呼ぶ運用)。
 *   - error 状態で赤枠 (`is-error`)。
 *
 * CSS は components.css の `.auth-twofa-cells` / `.auth-twofa-cell` を参照。
 */

const OTP_LENGTH = 6

export interface TwoFactorCellsProps {
  code: string
  onCodeChange: (next: string) => void
  /** 6 桁揃ったタイミングで自動 submit したい場合に渡す。 */
  onAutoSubmit?: () => void
  disabled?: boolean
  hasError?: boolean
}

export const TwoFactorCells = ({
  code,
  onCodeChange,
  onAutoSubmit,
  disabled,
  hasError,
}: TwoFactorCellsProps): JSX.Element => {
  const { t } = useTranslation()
  const inputsRef = useRef<(HTMLInputElement | null)[]>([])
  const digits = Array.from({ length: OTP_LENGTH }, (_, i) => code[i] ?? '')

  // onAutoSubmit を ref に固定 (callback の identity 変化を deps から除外)。
  const onAutoSubmitRef = useRef(onAutoSubmit)

  onAutoSubmitRef.current = onAutoSubmit

  // 同じ code を二度 submit させないためのガード。401 応答で disabled が
  // true → false に戻った時、code は 6 桁のまま残るため、ガード無しでは
  // useEffect が再発火して無限ループに陥る。code が一度空に戻れば ref を
  // クリアするので、ユーザーが同じ 6 桁を再入力しても再 submit できる。
  const lastSubmittedRef = useRef('')

  // クリア時 (空) / 6 桁完成時の処理を 1 つの effect に集約:
  //   - code='': lastSubmittedRef を reset + 先頭セルへ focus
  //     (初期マウント + 親が clear した直後の両方に対応)
  //   - code が 6 桁 + 未 submit のとき: 自動 submit を発火
  useEffect(() => {
    if (code.length === 0) {
      lastSubmittedRef.current = ''
      inputsRef.current[0]?.focus()

      return
    }
    if (code.length === OTP_LENGTH && !disabled && code !== lastSubmittedRef.current) {
      lastSubmittedRef.current = code
      onAutoSubmitRef.current?.()
    }
  }, [code, disabled])

  const setRef =
    (index: number) =>
    (el: HTMLInputElement | null): void => {
      inputsRef.current[index] = el
    }

  const handleChange =
    (index: number) =>
    (ev: ChangeEvent<HTMLInputElement>): void => {
      const raw = ev.target.value.replace(/\D/gu, '')
      if (raw === '') {
        const next = code.slice(0, index) + code.slice(index + 1)

        onCodeChange(next)

        return
      }
      const ch = raw.charAt(0)
      const next = (code.slice(0, index) + ch + code.slice(index + 1)).slice(0, OTP_LENGTH)

      onCodeChange(next)
      if (index < OTP_LENGTH - 1) inputsRef.current[index + 1]?.focus()
    }

  const handleKeyDown =
    (index: number) =>
    (ev: KeyboardEvent<HTMLInputElement>): void => {
      if (ev.key === 'Backspace' && digits[index] === '' && index > 0) {
        ev.preventDefault()

        const next = code.slice(0, index - 1) + code.slice(index)

        onCodeChange(next)
        inputsRef.current[index - 1]?.focus()
      } else if (ev.key === 'ArrowLeft' && index > 0) {
        ev.preventDefault()
        inputsRef.current[index - 1]?.focus()
      } else if (ev.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
        ev.preventDefault()
        inputsRef.current[index + 1]?.focus()
      }
    }

  const handlePaste = (ev: ClipboardEvent<HTMLInputElement>): void => {
    const pasted = ev.clipboardData.getData('text').replace(/\D/gu, '').slice(0, OTP_LENGTH)
    if (pasted === '') return
    ev.preventDefault()
    onCodeChange(pasted)

    const focusAt = Math.min(pasted.length, OTP_LENGTH - 1)

    inputsRef.current[focusAt]?.focus()
  }

  return (
    <div
      className={`auth-twofa-cells${hasError ? ' is-error' : ''}`}
      role="group"
      aria-label={t('auth.two_factor.cell_label')}
    >
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={setRef(index)}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          className="auth-twofa-cell"
          value={digit}
          disabled={disabled}
          onChange={handleChange(index)}
          onKeyDown={handleKeyDown(index)}
          onPaste={handlePaste}
          onFocus={ev => ev.target.select()}
          aria-label={`${t('auth.two_factor.cell_label')} ${index + 1}/${OTP_LENGTH}`}
        />
      ))}
    </div>
  )
}
